import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import { Reveal, Overline } from "@/components/Reveal";
import { PILLARS, FOUNDER, IMAGES } from "@/lib/data";
import { LINKEDIN_URL, SITE_URL } from "@/lib/site";
import { useLang } from "@/i18n";
import { Linkedin, ArrowRight } from "lucide-react";

const schema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "About Farmsons",
  url: `${SITE_URL}/about`,
  about: {
    "@type": "Organization",
    name: "Farmsons",
    foundingDate: "1985",
    founder: { "@type": "Person", name: "Zahid Shamim", jobTitle: "Founder", sameAs: LINKEDIN_URL },
  },
};

export default function About() {
  const { t, data } = useLang();
  const founder = data.founder || {};
  const paras = founder.paras || FOUNDER.paras;
  const tags = founder.tags || FOUNDER.tags;

  return (
    <>
      <SEO
        title="About Farmsons — Our Story & Founder Zahid Shamim"
        description="Farmsons carries a family legacy in agricultural trade since 1985. Led by founder Zahid Shamim (Jadavpur University, UCLA Anderson MBA, ex-Visa), it brings modern operating rigor to India's produce trade."
        keywords="Farmsons about, Zahid Shamim, farm produce trading company Kolkata, agricultural trade India since 1985"
        jsonLd={schema}
      />
      <PageHeader
        overline={t("about.overline")}
        title={t("about.title")}
        lede={t("about.lede")}
        image={IMAGES.himachal}
        imageAlt="Apple orchards beneath the Himalayan mountains, home of Farmsons' grower relationships"
      />

      <section className="pb-8 bg-[#fdfbf5]">
        <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-12">
          {PILLARS.map((c, i) => {
            const dp = data.pillars?.[i];
            return (
              <Reveal key={c.no} delay={i * 0.08}>
                <article className="border-s border-[#1d3a1f]/10 ps-6">
                  <span className="font-display font-extrabold text-5xl text-[#d3a53a]">{c.no}</span>
                  <h2 className="mt-4 font-display font-bold text-xl md:text-2xl">{dp?.title || c.title}</h2>
                  <p className="mt-3 text-[#5f6b57] leading-relaxed">{dp?.body || c.body}</p>
                </article>
              </Reveal>
            );
          })}
          </div>
      </section>

      {/* Founder */}
      <section id="founder" className="py-24 md:py-32 bg-[#ffffff] border-y border-[#1d3a1f]/5">
        <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <Reveal className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="relative group">
              <div className="absolute -inset-3 border border-[#d3a53a]/30 group-hover:border-[#d3a53a] hover:-translate-y-1.5 hover:shadow-xl transition-all duration-300" />
              <div className="relative overflow-hidden">
                <img src={IMAGES.founder} alt="Zahid Shamim, Founder of Farmsons" className="w-full h-[500px] object-cover object-top group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f12]/85 via-[#0d1f12]/10 to-transparent" />
                <span className="absolute bottom-4 start-4 text-xs font-bold tracking-[0.2em] uppercase text-[#d3a53a]">{founder.name || FOUNDER.name} · {founder.role || FOUNDER.role}</span>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            <Reveal><Overline>{t("about.founderOverline")}</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl tracking-tight">{t("about.founderHeading")}</h2>
            </Reveal>
            {paras.map((p, i) => (
              <Reveal key={i} delay={0.1 + i * 0.05}>
                <p className="mt-6 text-[#5f6b57] text-base md:text-lg leading-relaxed">{p}</p>
              </Reveal>
            ))}
            <Reveal delay={0.2}>
              <div className="mt-8 flex flex-wrap gap-3">
                {tags.map((tag) => (
                  <span key={tag} className="text-xs font-medium text-[#1d3a1f]/80 border border-[#1d3a1f]/10 px-4 py-2">{tag}</span>
                ))}
              </div>
              <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" data-testid="founder-linkedin"
                className="group mt-8 inline-flex items-center gap-2 text-sm font-bold text-[#5f6b57] hover:text-[#d3a53a] transition-colors duration-200">
                <Linkedin size={17} className="group-hover:text-[#d3a53a] transition-colors duration-200" /> LinkedIn
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#fdfbf5]">
        <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12">
          <Link to="/fruit-trading" className="inline-flex items-center gap-2 text-sm font-bold text-[#d3a53a] hover:text-[#1d3a1f] transition-colors">
            {t("about.exploreLink")} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </>
  );
}
